import { AgentCategory } from "~/shared/enums";
import type { AgentConfig, ValidationFunction } from "~/shared/types";
import { isValidUrlIncludingLocal, validateIsNotEmpty } from "~/utils/validation";

import AgentIcon from "./AgentIcon.vue";
import Icon from "./Icon.vue";

const SERVER_HTTP_IP_URL_REGEX = /^https?:\/\/\d{1,3}(?:\.\d{1,3}){3}(?::\d{1,5})?(?:[/?#].*)?$/;
const isValidServerIpUrl = (value: string) => SERVER_HTTP_IP_URL_REGEX.test(value.trim());

const validateServerUrl: ValidationFunction = (value) =>
  isValidUrlIncludingLocal(value) || isValidServerIpUrl(value)
    ? { isValid: true }
    : { isValid: false, error: "Enter a valid URL" };

const config: AgentConfig = {
  name: "Hermes",
  category: AgentCategory.ASSISTANT,
  icon: Icon,
  agentIcon: AgentIcon,
  color: "#ffd700",
  description: "Run multi-step workflows with a learning agent that remembers context and builds reusable skills",
  descriptionMarkdown: `## Overview

Delegate work to Hermes Agent directly from Dart. When you assign a task, Dart sends the task prompt to your Hermes Agent instance and posts the reply back into the task. Later comments on that same task continue the same conversation instead of starting over.

Dart also supports attaching images to tasks. Unsupported attachment types are skipped automatically.

## Setup guide

1. Host Hermes somewhere Dart can reach
2. Configure the API server:
   - Run \`hermes config set API_SERVER_ENABLED true\`
   - Run \`hermes config set API_SERVER_HOST 0.0.0.0\`
   - Run \`hermes config set API_SERVER_PORT 8642\`
   - Run \`hermes config set API_SERVER_KEY $(openssl rand -hex 32)\`
   - Grab the API key that's generated by the command
3. If not already open, open the port in firewall (e.g. run \`sudo ufw allow 8642/tcp\`)
4. Start the gateway with \`hermes gateway start\`
5. Open Dart settings, go to Agents, and choose Hermes Agent
6. Paste your server URL (e.g. \`http://<your-ip>:8642\`) and API key
7. Assign a task to Hermes Agent from Dart`,
  configFields: [
    {
      key: "serverUrl",
      type: "text",
      label: "Server URL",
      placeholder: "https://hermes.example",
      validate: validateServerUrl,
    },
    {
      key: "authToken",
      type: "password",
      label: "API key",
      placeholder: "Your Hermes API server key",
      validate: validateIsNotEmpty,
    },
  ],
};

export default config;
